import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt, FaFlask } from 'react-icons/fa';

const projects = [
  {
    id: 1,
    title: "CryptoTracker Dashboard",
    subtitle: "Real-time Cryptocurrency Monitoring",
    description: "A comprehensive cryptocurrency tracking platform with real-time price updates, portfolio management, and advanced charting. Built with React, integrates with CoinGecko API, and features responsive design with dark mode.",
    image: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=600&h=400&fit=crop",
    tags: ["React", "Redux", "Chart.js", "REST API"],
    github: "https://github.com/jitenmahajan/crypto-tracker",
    live: "https://crypto-tracker-jm.netlify.app",
    purity: 99.1,
    featured: true,
  },
  {
    id: 2,
    title: "TaskFlow Manager",
    subtitle: "Collaborative Project Management",
    description: "A full-stack project management application with real-time collaboration, drag-and-drop Kanban boards, team chat, and progress tracking. Features JWT authentication and role-based access control.",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop",
    tags: ["React", "Node.js", "MongoDB", "Socket.io"],
    github: "https://github.com/jitenmahajan/taskflow",
    live: "https://taskflow-manager.vercel.app",
    purity: 98.5,
    featured: true,
  },
  {
    id: 3,
    title: "WeatherWise App",
    subtitle: "Smart Weather Forecasting",
    description: "An intelligent weather application with 7-day forecasts, severe weather alerts, and location-based recommendations. Features beautiful UI with weather animations and supports multiple cities.",
    image: "https://images.unsplash.com/photo-1592210454359-9043f067919b?w=600&h=400&fit=crop",
    tags: ["React", "TypeScript", "OpenWeather API", "PWA"],
    github: "https://github.com/jitenmahajan/weatherwise",
    live: "https://weatherwise-jm.netlify.app",
    purity: 97.8,
    featured: true,
  },
  {
    id: 4,
    title: "DevBlog Platform",
    subtitle: "Developer Community Blog",
    description: "A modern blogging platform for developers with Markdown support, code syntax highlighting, tags, search functionality, and social features. Built with Next.js for optimal SEO.",
    image: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=600&h=400&fit=crop",
    tags: ["Next.js", "MongoDB", "Markdown", "Tailwind"],
    github: "https://github.com/jitenmahajan/devblog",
    live: "https://devblog-platform.vercel.app",
    purity: 96.2,
  },
  {
    id: 5,
    title: "FitTrack Pro",
    subtitle: "Fitness & Workout Tracker",
    description: "A comprehensive fitness tracking application with workout logging, progress charts, meal planning, and BMI calculator. Features custom workout plans and exercise library.",
    image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=600&h=400&fit=crop",
    tags: ["React", "Django", "PostgreSQL", "Chart.js"],
    github: "https://github.com/jitenmahajan/fittrack",
    live: "https://fittrack-pro.herokuapp.com",
    purity: 95.5,
  },
  {
    id: 6,
    title: "ShopEase E-commerce",
    subtitle: "Modern Shopping Experience",
    description: "A full-featured e-commerce platform with product catalog, shopping cart, secure checkout with Stripe, order tracking, and admin dashboard for inventory management.",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
    tags: ["MERN Stack", "Stripe", "JWT", "Redux"],
    github: "https://github.com/jitenmahajan/shopease",
    live: "https://shopease-store.netlify.app",
    purity: 94.8,
  },
];

const ProjectCard = ({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative group ${project.featured ? 'md:col-span-2 lg:col-span-1' : ''}`}
    >
      <div className="bg-gray-800/80 border border-green-900 rounded-lg overflow-hidden hover:border-bb-green transition-all duration-300 h-full">
        {/* Image Container */}
        <div className="relative h-48 overflow-hidden">
          <motion.img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.1 : 1 }}
            transition={{ duration: 0.3 }}
          />

          {/* Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"
            animate={{ opacity: isHovered ? 0.9 : 0.7 }}
          />

          {/* Purity Badge */}
          <div className="absolute top-4 right-4 bg-gray-900/90 border border-bb-green px-3 py-1 rounded-full flex items-center gap-2">
            <FaFlask className="text-bb-green-light text-sm" />
            <span className="text-bb-green-light text-sm font-bold">{project.purity}% Pure</span>
          </div>

          {/* Featured Badge */}
          {project.featured && (
            <motion.div
              className="absolute top-4 left-4 bg-bb-green px-3 py-1 rounded-full"
              animate={{
                boxShadow: isHovered
                  ? "0 0 20px rgba(0, 200, 83, 0.8)"
                  : "0 0 10px rgba(0, 200, 83, 0.4)"
              }}
            >
              <span className="text-white text-xs font-bold">FEATURED</span>
            </motion.div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-white mb-1">{project.title}</h3>
          <p className="text-bb-green-light text-sm mb-3">{project.subtitle}</p>
          <p className="text-gray-400 text-sm mb-4 line-clamp-3">{project.description}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-green-900/30 border border-green-800 rounded text-xs text-gray-300"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Links */}
          <div className="flex gap-4">
            <motion.a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 hover:text-bb-green-light transition-colors"
              whileHover={{ scale: 1.05 }}
            >
              <FaGithub />
              <span className="text-sm">Code</span>
            </motion.a>
            <motion.a
              href={project.live}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 hover:text-bb-green-light transition-colors"
              whileHover={{ scale: 1.05 }}
            >
              <FaExternalLinkAlt />
              <span className="text-sm">Live Demo</span>
            </motion.a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [filter, setFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);

  const filteredProjects = filter === 'featured'
    ? projects.filter(p => p.featured)
    : projects;

  const displayedProjects = showAll ? filteredProjects : filteredProjects.slice(0, 3);

  return (
    <section id="projects" className="py-20 px-4 bg-gray-900/50 relative">
      {/* Background Effect */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(0,200,83,0.05)_0%,_transparent_70%)]" />

      <div ref={ref} className="max-w-6xl mx-auto relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">My </span>
            <span className="text-bb-green-light">Lab Work</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-6">
            Each project is a carefully crafted formula, tested in production and proven to deliver results.
            Here's some of my finest work from the lab.
          </p>
          <div className="w-24 h-1 bg-bb-green mx-auto" />
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2 }}
          className="flex justify-center gap-4 mb-8"
        >
          {['all', 'featured'].map((filterOption) => (
            <motion.button
              key={filterOption}
              onClick={() => setFilter(filterOption)}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                filter === filterOption
                  ? 'bg-bb-green text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white border border-green-900'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {filterOption === 'all' ? 'All Projects' : 'Featured'}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {displayedProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Show More Button */}
        {filteredProjects.length > 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 }}
            className="text-center mt-12"
          >
            <motion.button
              onClick={() => setShowAll(!showAll)}
              className="px-8 py-3 border-2 border-bb-green text-bb-green font-bold rounded-lg hover:bg-bb-green hover:text-white transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {showAll ? 'Show Less' : 'View All Lab Work'}
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Projects;
